import React, { useState } from 'react';

const Stars = ({ value = 0 }) => {
    const v = Math.max(0, Math.min(5, Number(value)));
    return (
        <span className="review-stars" aria-label={`${v} out of 5`}>
            {'★'.repeat(Math.round(v)).padEnd(5, '☆')}
        </span>
    );
};

const ReviewCard = ({ review, showCourseTitle = false, isOwner = false, isAdmin = false, onEdit, onDelete, onAdminDelete }) => {
    // State for expanding long comments - must be called before any early returns
    const [isExpanded, setIsExpanded] = useState(false);
    const [imageError, setImageError] = useState(false);

    if (!review) return null;

    const { userName, userPhotoURL, rating, comment, verified, /* createdAt, */ courseTitle } = review;
    // TODO: Re-enable date formatting when dates are shown again
    // const dateText = createdAt?.toDate ? createdAt.toDate().toLocaleDateString() : '';

    // Helper function to format user names properly
    const formatUserName = (name) => {
        if (!name) return 'User';

        // If the name looks like an email username (all lowercase, no spaces)
        // and contains common email patterns, try to format it better
        if (name === name.toLowerCase() && !name.includes(' ')) {
            // Split camelCase or handle common patterns
            return name
                .replace(/([a-z])([A-Z])/g, '$1 $2') // Split camelCase
                .split(/[._-]/) // Split on dots, underscores, hyphens
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ')
                .trim() || name;
        }

        return name;
    };

    const displayName = formatUserName(userName);

    // Check if comment is long enough to need expansion
    const commentLength = (comment || '').length;
    const isLongComment = commentLength > 300;
    const displayComment = isLongComment && !isExpanded
        ? comment.slice(0, 300) + '...'
        : comment;

    const handleImageError = () => {
        setImageError(true);
    };

    const shouldShowImage = userPhotoURL && !imageError;

    return (
        <div className="review-card" id={`review-${review.id}`}>
            {/* Admin delete button in top-right corner */}
            {isAdmin && !isOwner && onAdminDelete && (
                <button
                    className="review-admin-delete-corner"
                    onClick={onAdminDelete}
                    title="Admin: Delete this review"
                >
                    🗑️
                </button>
            )}

            <div className="review-card-header">
                <div className="review-user">
                    <div className="review-avatar" aria-hidden>
                        {shouldShowImage ? (
                            <img
                                src={userPhotoURL}
                                alt={displayName || 'User'}
                                onError={handleImageError}
                            />
                        ) : (
                            <div className="default-avatar">
                                {(displayName || 'User').charAt(0)?.toUpperCase()}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="review-username">
                            <span>{displayName || 'User'}</span>
                            {verified && <span className="review-verified" title="Verified User">✓</span>}
                        </div>
                        {/* TODO: Re-enable review dates later */}
                        {/* {dateText && <div className="review-date">{dateText}</div>} */}
                    </div>
                </div>
                <Stars value={rating} />
            </div>

            {showCourseTitle && courseTitle && (
                <div className="review-course-chip">{courseTitle}</div>
            )}

            <div className="review-comment-section">
                <p className={`review-comment ${isExpanded ? 'expanded' : ''}`}>
                    {displayComment}
                </p>
                {isLongComment && (
                    <button
                        className="review-expand-btn"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? 'Show less' : 'Read more'}
                    </button>
                )}
            </div>

            {isOwner && (
                <div className="review-actions">
                    {onEdit && (
                        <button className="btn btn-secondary btn-small" onClick={onEdit}>Edit</button>
                    )}
                    {onDelete && (
                        <button className="btn btn-secondary btn-small" onClick={onDelete}>Delete</button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ReviewCard;
